import { useState } from "react";
import { activeMarkets, expansionMarkets, projectToSvg } from "../../data/countries";

/**
 * Clean minimal world map: uses a stylized world-map image as base
 * with animated SVG dot markers overlaid via lat/lng projection.
 * No heavy 3D globe — just a premium, editorial visual.
 */
export default function WorldMap() {
    const [hover, setHover] = useState(null);

    return (
        <div className="relative w-full">
            <div className="relative aspect-[2/1] w-full rounded-2xl overflow-hidden border border-white/10">
                <img
                    src="https://images.unsplash.com/photo-1562504208-03d85cc8c23e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhYnN0cmFjdCUyMHdvcmxkJTIwbWFwJTIwZG90c3xlbnwwfHx8fDE3ODM2ODY1NTd8MA&ixlib=rb-4.1.0&q=85"
                    alt="World map"
                    className="absolute inset-0 w-full h-full object-cover opacity-40"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#061A2E]/60 via-[#061A2E]/40 to-[#061A2E]/80" />

                <svg
                    viewBox="0 0 1000 500"
                    className="absolute inset-0 w-full h-full"
                    role="img"
                    aria-label="Exportfy export destinations map"
                >
                    {/* Active markets */}
                    {activeMarkets.map((m) => {
                        const { x, y } = projectToSvg(m);
                        return (
                            <g
                                key={m.code}
                                transform={`translate(${x} ${y})`}
                                onMouseEnter={() => setHover({ name: m.name, type: "active" })}
                                onMouseLeave={() => setHover(null)}
                                className="cursor-pointer"
                            >
                                <circle r="14" fill="#10B981" opacity="0.2" className="pulse-dot" />
                                <circle r="5.5" fill="#10B981" />
                                <circle r="2" fill="#ECFDF5" />
                                <text x="8" y="-8" fontSize="10" fill="#ECFDF5" className="font-semibold pointer-events-none" style={{ paintOrder: "stroke", stroke: "#061A2E", strokeWidth: 2 }}>
                                    {m.name}
                                </text>
                            </g>
                        );
                    })}

                    {/* Expansion markets */}
                    {expansionMarkets.map((m) => {
                        const { x, y } = projectToSvg(m);
                        return (
                            <g
                                key={m.code}
                                transform={`translate(${x} ${y})`}
                                onMouseEnter={() => setHover({ name: m.name, type: "expansion" })}
                                onMouseLeave={() => setHover(null)}
                                className="cursor-pointer"
                            >
                                <circle r="6" fill="none" stroke="#F59E0B" strokeWidth="1.4" />
                                <circle r="2" fill="#F59E0B" />
                            </g>
                        );
                    })}
                </svg>

                {hover && (
                    <div className="absolute bottom-4 left-4 bg-white/95 rounded-xl px-4 py-2 shadow-lg text-sm">
                        <div className="font-bold text-[#0A2540]">{hover.name}</div>
                        <div className="text-xs text-slate-500">
                            {hover.type === "active" ? "Active export market" : "Expansion market"}
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-6 flex flex-wrap gap-6 justify-center text-sm">
                <div className="flex items-center gap-2 text-white/85">
                    <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
                    </span>
                    Current markets · {activeMarkets.length}
                </div>
                <div className="flex items-center gap-2 text-white/85">
                    <span className="w-3 h-3 rounded-full border-2 border-amber-500" />
                    Expansion markets · {expansionMarkets.length}
                </div>
            </div>
        </div>
    );
}
